import { useCallback, useState } from "react";
import { useApi } from "../hooks/useApi";

interface CliToken {
  id: string;
  userEmail: string;
  tokenPrefix: string;
  deviceName: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function SettingsCliTokens() {
  const { data, loading, refetch } = useApi<{ tokens: CliToken[] }>(
    "/api/cli-tokens/all"
  );
  const [revoking, setRevoking] = useState<string | null>(null);

  const handleRevoke = useCallback(
    async (id: string, email: string) => {
      if (!confirm(`Revoke this CLI token for ${email}?`)) return;
      setRevoking(id);
      try {
        await fetch(`/api/cli-tokens/${id}/revoke-admin`, {
          method: "POST",
          credentials: "include"
        });
        refetch();
      } finally {
        setRevoking(null);
      }
    },
    [refetch]
  );

  const tokens = data?.tokens ?? [];
  const byUser = new Map<string, CliToken[]>();
  for (const t of tokens) {
    if (!byUser.has(t.userEmail)) byUser.set(t.userEmail, []);
    byUser.get(t.userEmail)!.push(t);
  }

  return (
    <div
      style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-lg)"
      }}
    >
      <h2
        style={{
          fontSize: "var(--font-size-md)",
          fontWeight: 600,
          marginBottom: "var(--space-sm)"
        }}
      >
        CLI Tokens ({tokens.filter((t) => !t.revokedAt).length} active)
      </h2>
      <p
        style={{
          color: "var(--color-text-secondary)",
          fontSize: "var(--font-size-sm)",
          marginBottom: "var(--space-md)"
        }}
      >
        Personal tokens minted via <code>gs-hive login</code>. To offboard someone, revoke all their tokens here.
      </p>

      {loading ? (
        <div style={{ color: "var(--color-text-secondary)" }}>Loading...</div>
      ) : tokens.length === 0 ? (
        <div style={{ color: "var(--color-text-secondary)" }}>
          No CLI tokens yet. Tell the team to run <code>yarn gs-hive login</code>.
        </div>
      ) : (
        <div>
          {Array.from(byUser.entries()).map(([email, userTokens]) => {
            const active = userTokens.filter((t) => !t.revokedAt);
            return (
              <div
                key={email}
                style={{
                  padding: "var(--space-md) 0",
                  borderBottom: "1px solid var(--color-border)"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "var(--space-xs)"
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{email}</div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--color-text-secondary)"
                    }}
                  >
                    {active.length} active / {userTokens.length} total
                  </div>
                </div>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13
                  }}
                >
                  <tbody>
                    {userTokens.map((t) => (
                      <tr
                        key={t.id}
                        style={{
                          opacity: t.revokedAt ? 0.5 : 1,
                          color: "var(--color-text-secondary)"
                        }}
                      >
                        <td style={{ padding: "4px 0" }}>
                          <code style={{ fontSize: 12 }}>{t.tokenPrefix}...</code>
                        </td>
                        <td style={{ padding: "4px 8px" }}>{t.deviceName ?? "-"}</td>
                        <td style={{ padding: "4px 8px" }}>
                          last used {timeAgo(t.lastUsedAt)}
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          {t.revokedAt ? (
                            <span style={{ color: "var(--color-error)" }}>
                              revoked {timeAgo(t.revokedAt)}
                            </span>
                          ) : (
                            <span>
                              expires {timeAgo(t.expiresAt)}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "4px 0", textAlign: "right" }}>
                          {!t.revokedAt && (
                            <button
                              onClick={() => handleRevoke(t.id, email)}
                              disabled={revoking === t.id}
                              style={{
                                padding: "2px 10px",
                                borderRadius: "var(--radius-md)",
                                border: "1px solid #dc4405",
                                background: "transparent",
                                color: "#dc4405",
                                fontSize: 11,
                                cursor: "pointer",
                                fontWeight: 600
                              }}
                            >
                              {revoking === t.id ? "..." : "Revoke"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
